import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { schoolsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import type { SchoolSendPreview } from '../../types/school';
import Modal from '../ui/Modal';
import { getErrorMessage } from '../../utils/errorMessage';

interface SendToSchoolModalProps {
  documentId: number | null;
  onClose: () => void;
  onSent: () => void;
}

/**
 * Egy játékos-dokumentum (kikérő, felmentés, igazolás) elküldése e-mailben a
 * játékos iskolájának, csatolmányként. Előtte megmutatja, kinek megy ki és ki
 * kap másolatot; ha nem küldhető (nincs iskola, nincs e-mail), megmondja, miért.
 */
const SendToSchoolModal: React.FC<SendToSchoolModalProps> = ({ documentId, onClose, onSent }) => {
  const { showToast } = useToast();
  const [preview, setPreview] = useState<SchoolSendPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [copyFamily, setCopyFamily] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (documentId == null) return;
    setPreview(null);
    setLoadError(null);
    setMessage('');
    setCopyFamily(true);
    schoolsAPI
      .getSendPreview(documentId)
      .then(setPreview)
      .catch((err) => {
        setLoadError(
          err.response?.status === 403
            ? 'Ezt a dokumentumot nem küldheted el az iskolának.'
            : 'Az adatok betöltése nem sikerült.'
        );
      });
  }, [documentId]);

  const handleSend = async () => {
    if (!preview) return;
    setSending(true);
    try {
      const result = await schoolsAPI.sendDocument(preview.documentId, {
        message: message.trim() || null,
        copyFamily,
      });
      showToast(
        result.cc.length > 0
          ? `Elküldve: ${result.sentTo} (másolat: ${result.cc.length} címzett)`
          : `Elküldve: ${result.sentTo}`
      );
      onSent();
    } catch (err: any) {
      showToast(getErrorMessage(err, 'Az e-mailt nem sikerült elküldeni.'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={documentId != null} onClose={onClose} title="Küldés az iskolának">
      <div className="space-y-4 pb-4">
        {loadError && <p className="text-sm text-red-600">{loadError}</p>}
        {!preview && !loadError && <p className="text-sm text-gray-500">Betöltés...</p>}

        {preview && (
          <>
            <div className="rounded-md bg-gray-50 p-3 text-sm">
              <p className="font-medium text-gray-900">
                {preview.documentTypeLabel} - {preview.playerName}
              </p>
              <p className="truncate text-xs text-gray-500">{preview.documentName}</p>
            </div>

            {!preview.canSend ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                <div>
                  <p className="font-medium">Nem küldhető el: {preview.problem}.</p>
                  <p className="mt-1 text-xs">
                    Az iskolát a játékos adatlapján (vagy a szülő a profilján) lehet megadni, az iskola e-mail címét
                    az Iskolák menüben.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Címzett</dt>
                    <dd className="text-gray-900">
                      {preview.schoolName}
                      {preview.schoolContactName && ` - ${preview.schoolContactName}`}
                      <span className="block text-gray-600">{preview.schoolEmail}</span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Másolatot kap</dt>
                    <dd className="text-gray-700">
                      {preview.ccRecipients.length === 0 ? (
                        <span className="text-gray-500">A játékosnak és a szülőknek nincs e-mail címe a rendszerben.</span>
                      ) : (
                        <label className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={copyFamily}
                            onChange={(e) => setCopyFamily(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span>{preview.ccRecipients.join(', ')}</span>
                        </label>
                      )}
                    </dd>
                  </div>
                </dl>

                <div>
                  <label htmlFor="school-message" className="mb-1 block text-sm font-medium text-gray-700">
                    Kísérőszöveg (nem kötelező)
                  </label>
                  <textarea
                    id="school-message"
                    rows={3}
                    maxLength={2000}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Pl. Kérjük, hogy a tanuló hiányzását igazoltnak tekintsék."
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    A dokumentum csatolmányként megy. Ha az iskola válaszol, a válasz neked érkezik.
                  </p>
                </div>

                {preview.sentToSchoolAt && (
                  <p className="text-xs text-amber-700">
                    Ezt a dokumentumot már elküldték ({preview.sentToSchoolEmail},{' '}
                    {new Date(preview.sentToSchoolAt).toLocaleString('hu-HU', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    ). Újraküldheted.
                  </p>
                )}
              </>
            )}
          </>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {preview?.canSend ? 'Mégse' : 'Bezárás'}
          </button>
          {preview?.canSend && (
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {sending ? 'Küldés...' : 'Küldés'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SendToSchoolModal;
